import { paymentConflictsListSchema, type PaymentConflictsList, type PaymentConflictsQuery } from "@openings/api-contract";
import { BaseHttpClient, baseOk, ApiOk, type ApiClientOptions } from "./base";
import {
  salonSchema,
  slugAvailabilityResponseSchema,
  type Salon,
  type SlugAvailabilityResponse,
  bookingPreviewResponseSchema,
  type BookingPreviewResponse,
  payoutsSummarySchema,
  appointmentSettlementStatusSchema,
  payoutsListSchema,
  payoutBreakdownSchema,
  retrySplitResponseSchema,
  transactionsListSchema,
  transactionGetSchema,
  refundsListSchema,
  cashTransfersListSchema,
  createLedgerAdjustmentResponseSchema,
  ledgerAdjustmentsListSchema,
  ledgerActivityListSchema,
  staffLedgerListSchema,
  staffPayoutHistorySchema,
  payoutScheduleSchema,
  activationResponseSchema,
  educationProgressCreateResponseSchema,
  educationProgressDeleteResponseSchema,
  type PayoutsSummary,
  type AppointmentSettlementStatus,
  type PayoutsList,
  type PayoutBreakdown,
  type RetrySplitResponse,
  type TransactionsList,
  type TransactionGet,
  type RefundsList,
  type CashTransfersList,
  type CreateLedgerAdjustmentInput,
  type CreateLedgerAdjustmentResponse,
  type LedgerAdjustmentsList,
  type LedgerActivityList,
  type StaffLedgerList,
  type StaffPayoutHistory,
  type PayoutSchedule,
  type UpdatePayoutScheduleInput,
  type ActivationResponse,
  type EducationProgressCreateResponse,
  type EducationProgressDeleteResponse,
} from "@openings/api-contract";

const salonOk = baseOk(salonSchema);
export type SalonDTO = Salon;
export type SalonGetResult = ApiOk<SalonDTO>;

const slugAvailabilityOk = baseOk(slugAvailabilityResponseSchema);
export type SlugAvailabilityResult = ApiOk<SlugAvailabilityResponse>;


const bookingPreviewOk = baseOk(bookingPreviewResponseSchema);
export type BookingPreviewResult = ApiOk<BookingPreviewResponse>;
export type {
  BookingPreview,
  BookingPreviewResponse,
} from "@openings/api-contract";

const payoutsSummaryOk = baseOk(payoutsSummarySchema);
const settlementStatusOk = baseOk(appointmentSettlementStatusSchema);
const payoutsListOk = baseOk(payoutsListSchema);
const payoutBreakdownOk = baseOk(payoutBreakdownSchema);
const transactionsListOk = baseOk(transactionsListSchema);
const transactionGetOk = baseOk(transactionGetSchema);
const refundsListOk = baseOk(refundsListSchema);
const cashTransfersListOk = baseOk(cashTransfersListSchema);
const retrySplitOk = baseOk(retrySplitResponseSchema);
const staffLedgerListOk = baseOk(staffLedgerListSchema);
const staffPayoutHistoryOk = baseOk(staffPayoutHistorySchema);
const payoutScheduleOk = baseOk(payoutScheduleSchema);
const activationOk = baseOk(activationResponseSchema);
const educationProgressOk = baseOk(educationProgressCreateResponseSchema);
const educationProgressDeleteOk = baseOk(educationProgressDeleteResponseSchema);
export type PayoutsSummaryResult = ApiOk<PayoutsSummary>;
export type AppointmentSettlementStatusResult =
  ApiOk<AppointmentSettlementStatus>;
export type PayoutsListResult = ApiOk<PayoutsList>;
export type PayoutBreakdownResult = ApiOk<PayoutBreakdown>;
export type TransactionsListResult = ApiOk<TransactionsList>;
export type TransactionGetResult = ApiOk<TransactionGet>;
export type RefundsListResult = ApiOk<RefundsList>;
export type CashTransfersListResult = ApiOk<CashTransfersList>;
export type RetrySplitResult = ApiOk<RetrySplitResponse>;
export type StaffLedgerListResult = ApiOk<StaffLedgerList>;
export type StaffPayoutHistoryResult = ApiOk<StaffPayoutHistory>;
export type PayoutScheduleResult = ApiOk<PayoutSchedule>;
export type ActivationResult = ApiOk<ActivationResponse>;
export type EducationProgressResult = ApiOk<EducationProgressCreateResponse>;
export type EducationProgressDeleteResult =
  ApiOk<EducationProgressDeleteResponse>;
export type { ActivationResponse } from "@openings/api-contract";

class SalonPayoutsClient extends BaseHttpClient {
  getSummary(salonId: string): Promise<PayoutsSummaryResult> {
    return this.get<PayoutsSummaryResult>(
      `/v1/salons/${encodeURIComponent(salonId)}/payouts/summary`,
      payoutsSummaryOk,
    );
  }
  /** Who settles the owner's appointment card payments, per channel. */
  getSettlement(salonId: string): Promise<AppointmentSettlementStatusResult> {
    return this.get<AppointmentSettlementStatusResult>(
      `/v1/salons/${encodeURIComponent(salonId)}/payments/settlement`,
      settlementStatusOk,
    );
  }
  list(
    salonId: string,
    opts: { limit?: number; startingAfter?: string } = {},
  ): Promise<PayoutsListResult> {
    const qs = new URLSearchParams();
    if (opts.limit != null) qs.set("limit", String(opts.limit));
    if (opts.startingAfter) qs.set("starting_after", opts.startingAfter);
    const suffix = qs.toString() ? `?${qs}` : "";
    return this.get<PayoutsListResult>(
      `/v1/salons/${encodeURIComponent(salonId)}/payouts${suffix}`,
      payoutsListOk,
    );
  }
  breakdown(
    salonId: string,
    payoutId: string,
  ): Promise<PayoutBreakdownResult> {
    return this.get<PayoutBreakdownResult>(
      `/v1/salons/${encodeURIComponent(salonId)}/payouts/${encodeURIComponent(
        payoutId,
      )}/breakdown`,
      payoutBreakdownOk,
    );
  }
  transactions(
    salonId: string,
    opts: {
      range?: "30d" | "90d";
      start?: string;
      end?: string;
      timezone?: string;
    } = {},
  ): Promise<TransactionsListResult> {
    const qs = new URLSearchParams();
    if (opts.start && opts.end) {
      qs.set("start", opts.start);
      qs.set("end", opts.end);
      if (opts.timezone) qs.set("timezone", opts.timezone);
    } else if (opts.range) {
      qs.set("range", opts.range);
    }
    const suffix = qs.toString() ? `?${qs}` : "";
    return this.get<TransactionsListResult>(
      `/v1/salons/${encodeURIComponent(salonId)}/payouts/transactions${suffix}`,
      transactionsListOk,
    );
  }
  /**
   * Single-transaction lookup by stable row id (`checkout:<id>` /
   * `deposit:<appointmentId>`) or bare appointment id. Fee enrichment is
   * scoped to the one charge server-side (no window scanning).
   */
  getTransaction(
    salonId: string,
    transactionId: string,
  ): Promise<TransactionGetResult> {
    return this.get<TransactionGetResult>(
      `/v1/salons/${encodeURIComponent(salonId)}/payouts/transactions/${encodeURIComponent(
        transactionId,
      )}`,
      transactionGetOk,
    );
  }
  listPaymentConflicts(salonId: string, opts: PaymentConflictsQuery = {}): Promise<ApiOk<PaymentConflictsList>> {
    const query = new URLSearchParams();
    if (opts.appointmentId) query.set("appointmentId", opts.appointmentId);
    if (opts.limit != null) query.set("limit", String(opts.limit));
    if (opts.offset != null) query.set("offset", String(opts.offset));
    return this.get(`/v1/salons/${encodeURIComponent(salonId)}/payment-conflicts?${query}`, baseOk(paymentConflictsListSchema));
  }

  /** Refund history (checkout + deposit refunds), newest first. */
  listRefunds(
    salonId: string,
    opts: {
      start?: string;
      end?: string;
      timezone?: string;
      limit?: number;
      offset?: number;
    } = {},
  ): Promise<RefundsListResult> {
    const qs = new URLSearchParams();
    if (opts.start && opts.end) {
      qs.set("start", opts.start);
      qs.set("end", opts.end);
      if (opts.timezone) qs.set("timezone", opts.timezone);
    }
    if (opts.limit != null) qs.set("limit", String(opts.limit));
    if (opts.offset != null) qs.set("offset", String(opts.offset));
    const suffix = qs.toString() ? `?${qs}` : "";
    return this.get<RefundsListResult>(
      `/v1/salons/${encodeURIComponent(salonId)}/refunds${suffix}`,
      refundsListOk,
    );
  }
  retrySplit(
    salonId: string,
    appointmentId: string,
  ): Promise<RetrySplitResult> {
    return this.post<RetrySplitResult>(
      `/v1/salons/${encodeURIComponent(salonId)}/payouts/transactions/${encodeURIComponent(
        appointmentId,
      )}/retry-split`,
      {},
      retrySplitOk,
    );
  }
  transfers(
    salonId: string,
    opts: { start: string; end: string; timezone?: string },
  ): Promise<CashTransfersListResult> {
    const qs = new URLSearchParams({ start: opts.start, end: opts.end });
    if (opts.timezone) qs.set("timezone", opts.timezone);
    return this.get<CashTransfersListResult>(
      `/v1/salons/${encodeURIComponent(salonId)}/transfers?${qs}`,
      cashTransfersListOk,
    );
  }
  staffLedger(salonId: string): Promise<StaffLedgerListResult> {
    return this.get<StaffLedgerListResult>(
      `/v1/salons/${encodeURIComponent(salonId)}/staff-ledger`,
      staffLedgerListOk,
    );
  }
  staffPayouts(
    salonId: string,
    staffId: string,
  ): Promise<StaffPayoutHistoryResult> {
    return this.get<StaffPayoutHistoryResult>(
      `/v1/salons/${encodeURIComponent(salonId)}/staff-ledger/${encodeURIComponent(
        staffId,
      )}/payouts`,
      staffPayoutHistoryOk,
    );
  }
  getPayoutSchedule(salonId: string): Promise<PayoutScheduleResult> {
    return this.get<PayoutScheduleResult>(
      `/v1/salons/${encodeURIComponent(salonId)}/payout-schedule`,
      payoutScheduleOk,
    );
  }
  updatePayoutSchedule(
    salonId: string,
    input: UpdatePayoutScheduleInput,
  ): Promise<PayoutScheduleResult> {
    return this.patch<PayoutScheduleResult>(
      `/v1/salons/${encodeURIComponent(salonId)}/payout-schedule`,
      input,
      payoutScheduleOk,
    );
  }
}

const ledgerAdjustmentOk = baseOk(createLedgerAdjustmentResponseSchema);
const ledgerAdjustmentsListOk = baseOk(ledgerAdjustmentsListSchema);
const ledgerActivityListOk = baseOk(ledgerActivityListSchema);
export type CreateLedgerAdjustmentResult =
  ApiOk<CreateLedgerAdjustmentResponse>;
export type LedgerAdjustmentsListResult = ApiOk<LedgerAdjustmentsList>;
export type LedgerActivityListResult = ApiOk<LedgerActivityList>;

class SalonLedgerClient extends BaseHttpClient {
  createAdjustment(
    salonId: string,
    input: CreateLedgerAdjustmentInput,
  ): Promise<CreateLedgerAdjustmentResult> {
    return this.post<CreateLedgerAdjustmentResult>(
      `/v1/salons/${encodeURIComponent(salonId)}/ledger/adjustments`,
      input,
      ledgerAdjustmentOk,
    );
  }
  listAdjustments(
    salonId: string,
    opts: { limit?: number } = {},
  ): Promise<LedgerAdjustmentsListResult> {
    const qs = new URLSearchParams();
    if (opts.limit != null) qs.set("limit", String(opts.limit));
    const suffix = qs.toString() ? `?${qs}` : "";
    return this.get<LedgerAdjustmentsListResult>(
      `/v1/salons/${encodeURIComponent(salonId)}/ledger/adjustments${suffix}`,
      ledgerAdjustmentsListOk,
    );
  }
  activity(
    salonId: string,
    opts: { limit?: number; offset?: number } = {},
  ): Promise<LedgerActivityListResult> {
    const qs = new URLSearchParams();
    if (opts.limit != null) qs.set("limit", String(opts.limit));
    if (opts.offset != null) qs.set("offset", String(opts.offset));
    const suffix = qs.toString() ? `?${qs}` : "";
    return this.get<LedgerActivityListResult>(
      `/v1/salons/${encodeURIComponent(salonId)}/ledger/activity${suffix}`,
      ledgerActivityListOk,
    );
  }
}

export class SalonsClient extends BaseHttpClient {
  payouts: SalonPayoutsClient;
  ledger: SalonLedgerClient;
  constructor(opts: ApiClientOptions) {
    super(opts);
    this.payouts = new SalonPayoutsClient(opts);
    this.ledger = new SalonLedgerClient(opts);
  }
  getById(id: string): Promise<SalonGetResult> {
    return this.get<SalonGetResult>(
      `/v1/salons/${encodeURIComponent(id)}`,
      salonOk,
    );
  }
  getBySlug(slug: string): Promise<SalonGetResult> {
    const qs = new URLSearchParams({ slug });
    return this.get<SalonGetResult>(
      `/v1/salons/by-slug?${qs.toString()}`,
      salonOk,
    );
  }
  getByDomain(domain: string): Promise<SalonGetResult> {
    const qs = new URLSearchParams({ domain });
    return this.get<SalonGetResult>(
      `/v1/salons/by-domain?${qs.toString()}`,
      salonOk,
    );
  }
  checkSlug(slug: string, excludeId?: string): Promise<SlugAvailabilityResult> {
    const qs = new URLSearchParams({ slug });
    if (excludeId) {
      qs.set("excludeId", excludeId);
    }
    return this.get<SlugAvailabilityResult>(
      `/v1/salons/check-slug?${qs.toString()}`,
      slugAvailabilityOk,
    );
  }

  /**
   * Activation funnel for the Getting Started checklist + lifecycle nudges.
   * Owner-scoped; milestones are computed from data existence server-side.
   */
  getActivation(salonId: string): Promise<ActivationResult> {
    return this.get<ActivationResult>(
      `/v1/salons/${encodeURIComponent(salonId)}/activation`,
      activationOk,
    );
  }

  /** Record a per-user education progress key (idempotent). Owner-scoped. */
  recordEducationProgress(
    salonId: string,
    key: string,
  ): Promise<EducationProgressResult> {
    return this.post<EducationProgressResult>(
      `/v1/salons/${encodeURIComponent(salonId)}/education-progress`,
      { key },
      educationProgressOk,
    );
  }

  deleteEducationProgress(
    salonId: string,
    key: string,
  ): Promise<EducationProgressDeleteResult> {
    return this.del<EducationProgressDeleteResult>(
      `/v1/salons/${encodeURIComponent(salonId)}/education-progress/${encodeURIComponent(key)}`,
      educationProgressDeleteOk,
    );
  }

  /**
   * Booking data for the standalone booking page and the feedback page:
   * schedules, services, members, categories.
   */
  getBookingPreview(input: { salonId: string }): Promise<BookingPreviewResult> {
    return this.get<BookingPreviewResult>(
      `/v1/salons/${encodeURIComponent(input.salonId)}/booking-preview`,
      bookingPreviewOk,
    );
  }
}
